import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm, usePrompt } from "@/features/confirm/ConfirmProvider";
import { DealPickerDialog } from "@/components/DealPicker";

interface LinkedDeal {
  dealId: string;
  hubspotDealId: string | null;
  dealName: string | null;
  companyName: string | null;
  notes: string | null;
}

interface Suggestion {
  dealId: string;
  hubspotDealId: string;
  dealName: string | null;
  companyName: string | null;
  rank: number;
  reason: string;
}

interface CustomerRow {
  stripeCustomerId: string;
  name: string | null;
  email: string | null;
  domain: string | null;
  description: string | null;
  archivedAt: string | null;
  invoiceCount: number;
  deals: LinkedDeal[];
  headOffice: boolean;
  suggestions: Suggestion[];
}

type TabKey = "all" | "unlinked" | "linked" | "headoffice" | "archived";

const customersSearch = z.object({ q: z.string().optional(), tab: z.enum(["all", "unlinked", "linked", "headoffice", "archived"]).optional() });

export const Route = createFileRoute("/admin/customers")({
  validateSearch: customersSearch,
  component: CustomersPage,
});

function CustomersPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const promptInput = usePrompt();
  const tab: TabKey = search.tab ?? "all";
  const [busy, setBusy] = useState<string | null>(null);
  // Deal-picker dialog state: which customer + which mutation it feeds.
  const [picker, setPicker] = useState<{ mode: "link" | "add"; customer: CustomerRow } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "customers", search.q ?? ""],
    queryFn: () => api.get<{ customers: CustomerRow[] }>("/api/admin/customers", { searchParams: { q: search.q } }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "customers"] });
    qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
  };

  function mutationError(label: string) {
    return (err: unknown) => toast.err(err instanceof ApiError ? err.message : label);
  }

  const link = useMutation({
    mutationFn: ({ id, hubspot_deal_id }: { id: string; hubspot_deal_id: string }) =>
      api.post(`/api/admin/customers/${encodeURIComponent(id)}/link`, { hubspot_deal_id }),
    onSuccess: () => { toast.ok("Linked."); invalidate(); },
    onError: mutationError("Couldn't link."),
    onSettled: () => setBusy(null),
  });

  const addDeal = useMutation({
    mutationFn: ({ id, hubspot_deal_id, notes }: { id: string; hubspot_deal_id: string; notes?: string }) =>
      api.post<{ nowMultiDeal?: boolean }>(`/api/admin/customers/${encodeURIComponent(id)}/deals`, { hubspot_deal_id, notes }),
    onSuccess: (res: { nowMultiDeal?: boolean }) => {
      toast.ok(res?.nowMultiDeal ? "Deal added — customer is now head-office." : "Deal added.");
      invalidate();
    },
    onError: mutationError("Couldn't add deal."),
    onSettled: () => setBusy(null),
  });

  const removeDeal = useMutation({
    mutationFn: ({ id, dealId }: { id: string; dealId: string }) =>
      api.delete<{ commissionsReversed?: number }>(`/api/admin/customers/${encodeURIComponent(id)}/deals/${encodeURIComponent(dealId)}`),
    onSuccess: (res: { commissionsReversed?: number }) => {
      toast.ok(res?.commissionsReversed ? `Deal removed — ${res.commissionsReversed} commission(s) reversed.` : "Deal removed.");
      invalidate();
    },
    onError: mutationError("Couldn't remove deal."),
    onSettled: () => setBusy(null),
  });

  const unlink = useMutation({
    mutationFn: ({ id }: { id: string }) => api.post(`/api/admin/customers/${encodeURIComponent(id)}/unlink`),
    onSuccess: () => { toast.ok("Unlinked."); invalidate(); },
    onError: mutationError("Couldn't unlink."),
    onSettled: () => setBusy(null),
  });

  const archive = useMutation({
    mutationFn: ({ id, archive: doArchive, reason }: { id: string; archive: boolean; reason?: string }) =>
      api.post(`/api/admin/customers/${encodeURIComponent(id)}/archive`, { archive: doArchive, reason }),
    onSuccess: (_, vars) => { toast.ok(vars.archive ? "Archived." : "Restored."); invalidate(); },
    onError: mutationError("Couldn't toggle archive."),
    onSettled: () => setBusy(null),
  });

  const autoLink = useMutation({
    mutationFn: ({ dry }: { dry: boolean }) =>
      api.post<{ scanned: number; linked: number; commissionsCreated: number; noMatch: number; ambiguous: unknown[] }>(
        "/api/reconcile/auto-link", undefined, { searchParams: { dry: dry ? "1" : "" } },
      ),
    onSuccess: (res, vars) => {
      toast.ok(
        `${vars.dry ? "Dry-run: " : ""}Scanned ${res.scanned}. Linked ${res.linked}, ambiguous ${res.ambiguous.length}, no-match ${res.noMatch}, commissions created ${res.commissionsCreated}.`,
        9000,
      );
      invalidate();
    },
    onError: mutationError("Auto-link failed."),
  });

  async function onAutoLink() {
    const ok = await confirm({
      title: "Auto-link customers",
      prompt: "Run the multi-strategy matcher (metadata → email → domain → company / deal name) across every unlinked Stripe invoice. Unambiguous matches are auto-linked; the rest surface on /admin/reconcile.",
      confirmLabel: "Auto-link",
    });
    if (ok) autoLink.mutate({ dry: false });
  }

  // 1:1 link / relink (replaces all existing links) — opens the deal picker.
  function onLink(c: CustomerRow) {
    setPicker({ mode: "link", customer: c });
  }

  // Add a deal to make / grow a head-office customer — opens the deal picker.
  function onAddDeal(c: CustomerRow) {
    setPicker({ mode: "add", customer: c });
  }

  function onPickDeal(hubspotDealId: string) {
    if (!picker) return;
    const { mode, customer } = picker;
    setPicker(null);
    setBusy(customer.stripeCustomerId);
    if (mode === "link") link.mutate({ id: customer.stripeCustomerId, hubspot_deal_id: hubspotDealId });
    else addDeal.mutate({ id: customer.stripeCustomerId, hubspot_deal_id: hubspotDealId });
  }

  async function onRemoveDeal(c: CustomerRow, d: LinkedDeal) {
    const ok = await confirm({
      title: "Remove deal linkage",
      prompt: `Unlink ${d.dealName ?? d.dealId} from this customer? Its active commission will be reversed and this customer's invoices for that deal cleared.`,
      confirmLabel: "Remove",
      tone: "destructive",
    });
    if (ok) { setBusy(c.stripeCustomerId); removeDeal.mutate({ id: c.stripeCustomerId, dealId: d.dealId }); }
  }

  async function onUnlink(c: CustomerRow) {
    const ok = await confirm({
      title: "Unlink customer",
      prompt: `Remove all ${c.deals.length} deal linkage(s)? Active commissions are reversed and invoices cleared.`,
      confirmLabel: "Unlink all",
      tone: "destructive",
    });
    if (ok) { setBusy(c.stripeCustomerId); unlink.mutate({ id: c.stripeCustomerId }); }
  }

  async function onArchive(c: CustomerRow) {
    if (c.archivedAt) { setBusy(c.stripeCustomerId); archive.mutate({ id: c.stripeCustomerId, archive: false }); return; }
    const reason = await promptInput({
      title: "Archive customer",
      prompt: "Park this customer out of the active list. You can restore it later.",
      label: "Reason (optional)",
      confirmLabel: "Archive",
      required: false,
    });
    if (reason !== null) { setBusy(c.stripeCustomerId); archive.mutate({ id: c.stripeCustomerId, archive: true, reason: reason || undefined }); }
  }

  function onLinkSuggestion(c: CustomerRow, s: Suggestion) {
    if (!s.hubspotDealId) return;
    setBusy(c.stripeCustomerId);
    link.mutate({ id: c.stripeCustomerId, hubspot_deal_id: s.hubspotDealId });
  }

  const all = data?.customers ?? [];
  const counts = useMemo(() => ({
    all: all.length,
    unlinked: all.filter((c) => !c.archivedAt && c.deals.length === 0).length,
    linked: all.filter((c) => !c.archivedAt && c.deals.length >= 1).length,
    headoffice: all.filter((c) => !c.archivedAt && c.headOffice).length,
    archived: all.filter((c) => c.archivedAt).length,
  }), [all]);

  const rows = useMemo(() => all.filter((c) => {
    switch (tab) {
      case "unlinked": return !c.archivedAt && c.deals.length === 0;
      case "linked": return !c.archivedAt && c.deals.length >= 1;
      case "headoffice": return !c.archivedAt && c.headOffice;
      case "archived": return !!c.archivedAt;
      default: return true;
    }
  }), [all, tab]);

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: "all", label: `All (${counts.all})` },
    { key: "unlinked", label: `Unlinked (${counts.unlinked})` },
    { key: "linked", label: `Linked (${counts.linked})` },
    { key: "headoffice", label: `Head office (${counts.headoffice})` },
    { key: "archived", label: `Archived (${counts.archived})` },
  ];

  return (
    <>
      <h1>Stripe customers</h1>
      <Panel>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          A customer linked to <strong>one</strong> deal cascades attribution to all its invoices. Linking a{" "}
          <strong>second</strong> deal makes it a <strong>head office</strong>: no auto-cascade, and invoices for each
          deal are linked individually on <code>/admin/invoices</code>.
        </p>
        <form className="inline filters" style={{ gap: 12 }} onSubmit={(e) => e.preventDefault()}>
          <input
            type="search"
            placeholder="Search Stripe id / name / email / domain / description"
            value={search.q ?? ""}
            onChange={(e) => navigate({ search: { ...search, q: e.target.value || undefined } })}
            style={{ minWidth: 300, flex: 1 }}
          />
          <button type="button" onClick={() => autoLink.mutate({ dry: true })} disabled={autoLink.isPending}>
            {autoLink.isPending ? "Running…" : "Dry-run auto-link"}
          </button>
          <button type="button" className="primary" onClick={onAutoLink} disabled={autoLink.isPending}>
            {autoLink.isPending ? "Running…" : "Auto-link all"}
          </button>
        </form>
        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={tab === t.key ? "primary" : undefined}
              style={{ padding: "4px 12px", fontSize: 13 }}
              onClick={() => navigate({ search: { ...search, tab: t.key === "all" ? undefined : t.key } })}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Panel>

      <Panel padding="tight">
        {isLoading ? (
          <EmptyState icon="generic" title="Loading…" body="Fetching customers." />
        ) : rows.length === 0 ? (
          <EmptyState icon="generic" title="No customers" body="Nothing in this view." />
        ) : (
          <DataTable>
            <table className="table-cards">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Identity</th>
                  <th>Invoices</th>
                  <th>Linked deals</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const rowBusy = busy === c.stripeCustomerId;
                  return (
                    <tr key={c.stripeCustomerId} style={c.archivedAt ? { opacity: 0.55 } : undefined}>
                      <td data-label="Customer">
                        <div style={{ fontWeight: 600 }}>{c.name ?? "—"}</div>
                        {c.description && <div className="muted" style={{ fontSize: 12 }}>{c.description}</div>}
                        <code style={{ fontSize: 10 }}>{c.stripeCustomerId}</code>
                        {c.headOffice && <span className="badge" style={{ marginLeft: 6 }}>head office</span>}
                        {c.archivedAt && <span className="badge" style={{ marginLeft: 6 }}>archived</span>}
                      </td>
                      <td data-label="Identity" className="muted" style={{ fontSize: 12 }}>
                        <div>{c.email ?? "—"}</div>
                        <div>{c.domain ?? ""}</div>
                      </td>
                      <td data-label="Invoices">{c.invoiceCount}</td>
                      <td data-label="Linked deals">
                        {c.deals.length === 0 ? (
                          <div>
                            <span className="muted" style={{ fontSize: 12 }}>unlinked</span>
                            {c.suggestions.length > 0 && (
                              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                                {c.suggestions.map((s) => (
                                  <button
                                    key={s.dealId}
                                    type="button"
                                    title={s.reason}
                                    disabled={rowBusy}
                                    style={{ padding: "3px 8px", fontSize: 11, textAlign: "left" }}
                                    onClick={() => onLinkSuggestion(c, s)}
                                  >
                                    + {s.dealName ?? s.companyName ?? s.hubspotDealId}
                                    <span className="muted" style={{ marginLeft: 6 }}>({s.rank})</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {c.deals.map((d) => (
                              <div key={d.dealId} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ fontSize: 13 }}>{d.dealName ?? d.companyName ?? d.hubspotDealId ?? d.dealId}</span>
                                <button
                                  type="button"
                                  disabled={rowBusy}
                                  title="Remove this linkage"
                                  style={{ padding: "1px 7px", fontSize: 11 }}
                                  onClick={() => onRemoveDeal(c, d)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td data-label="Actions">
                        <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                          <button style={btn} disabled={rowBusy} onClick={() => onLink(c)}>
                            {c.deals.length > 0 ? "Replace" : "Link"}
                          </button>
                          <button style={btn} disabled={rowBusy} onClick={() => onAddDeal(c)}>Add deal</button>
                          {c.deals.length > 0 && (
                            <button style={btn} disabled={rowBusy} onClick={() => onUnlink(c)}>Unlink</button>
                          )}
                          <button style={btn} disabled={rowBusy} onClick={() => onArchive(c)}>
                            {c.archivedAt ? "Restore" : "Archive"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </DataTable>
        )}
      </Panel>

      {picker && (
        <DealPickerDialog
          title={
            picker.mode === "add"
              ? "Add a deal (head office)"
              : picker.customer.deals.length > 0
                ? "Replace with a single deal"
                : "Link customer to deal"
          }
          prompt={
            picker.mode === "add"
              ? "Add another HubSpot deal to this customer. Existing invoices keep their attribution; new deals require per-invoice linking on /admin/invoices."
              : picker.customer.headOffice
                ? `Replace this head-office customer's ${picker.customer.deals.length} linked deals with ONE deal? Existing commissions on the detached deals will be reversed.`
                : "Future invoices for this customer auto-attach to this deal."
          }
          actionLabel={picker.mode === "add" ? "Add deal" : "Link"}
          onPick={onPickDeal}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  );
}

const btn: React.CSSProperties = { padding: "4px 10px", fontSize: 12 };
